import React, { useEffect, Suspense, lazy, useState } from 'react';
import { Stack } from '@fiverr-private/layout_components';
import {
    registerFloatingChatListeners,
    unregisterFloatingChatListeners,
} from '@fiverr-private/go_floating_chat_triggers';
import { type Seller as SellerType, type Currency } from '@fiverr-private/go_chat_base';
import type { PricingConfiguration, PricingContext } from '@fiverr-private/price_upfront_components';

const FloatingChatEntry = lazy(() => import(/* webpackChunkName: "go_floating_chat" */ '../FloatingChatEntry'));

interface Payload {
    data?: {
        recipientUsername: string;
        showConversationSavedTooltip: boolean;
        seller: SellerType;
    };
}

export interface FloatingChatProps {
    currentUsername: string;
    currency?: Currency;
    rollouts: Record<string, boolean>;
    priceUpfrontContext?: PricingContext;
    priceUpfrontConfiguration?: PricingConfiguration;
    isPriceUpfrontEnabled?: boolean;
}

export const FloatingChat = ({
    currentUsername,
    currency,
    rollouts,
    priceUpfrontContext,
    priceUpfrontConfiguration,
    isPriceUpfrontEnabled,
}: FloatingChatProps) => {
    const [recipientUsername, setRecipientUsername] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showConversationSavedTooltip, setShowConversationSavedTooltip] = useState(false);
    const [sellerData, setSellerData] = useState<SellerType | null>(null);
    useEffect(() => {
        const callback = (payload?: Payload) => {
            if (payload?.data?.recipientUsername) {
                setRecipientUsername(payload?.data?.recipientUsername);
                setShowConversationSavedTooltip(payload?.data?.showConversationSavedTooltip);
                setIsModalOpen(true);
            }
            if (payload?.data?.seller) {
                setSellerData(payload?.data?.seller);
            }
        };
        registerFloatingChatListeners({ GO_OPEN_FLOATING_CHAT: callback });

        return () => {
            unregisterFloatingChatListeners({ GO_OPEN_FLOATING_CHAT: callback });
        };
    }, []);

    if (!recipientUsername) return null;
    return (
        <Suspense fallback={null}>
            <Stack direction="column">
                <FloatingChatEntry
                    currentUsername={currentUsername}
                    recipientUsername={recipientUsername}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    showConversationSavedTooltip={showConversationSavedTooltip}
                    seller={sellerData ?? undefined}
                    currency={currency}
                    rollouts={rollouts}
                    priceUpfrontContext={priceUpfrontContext}
                    priceUpfrontConfiguration={priceUpfrontConfiguration}
                    isPriceUpfrontEnabled={isPriceUpfrontEnabled}
                />
            </Stack>
        </Suspense>
    );
};
